import { X } from "lucide-react";
import type { ReactNode } from "react";

import { useCloseOnBack } from "../lib/useCloseOnBack";
import { useScrollLock } from "../lib/useScrollLock";

interface Props {
  title: ReactNode;
  /** ヘッダー右側(X の左)の操作 */
  actions?: ReactNode;
  onClose: () => void;
  /** シートの上に開くシート(日別予定→予定編集など)は1段上に重ねる */
  elevated?: boolean;
  /** md 以上で画面の縦中央に置く(既定は下寄りのカード)。詳細のタグ面など単発の道具向け */
  center?: boolean;
  /** パネルの地の色。入力カードを並べる画面(予定・日記)はアプリ背景色が映える */
  tone?: "card" | "bg";
  children: ReactNode;
}

/**
 * 下から出るシートの外殻。スマホは全幅、md 以上は中央カード(--sheet-w)。
 * ヘッダー(タイトル+X)と閉じる操作(X・背景クリック・戻る)を持ち、
 * 中身は幅を持たない(幅は容れ物が決める)。
 */
export default function SheetShell({
  title,
  actions,
  onClose,
  elevated = false,
  center = false,
  tone = "card",
  children,
}: Props) {
  useCloseOnBack(onClose);
  useScrollLock();
  return (
    <div
      className={`fixed inset-0 flex flex-col justify-end bg-black/40 ${
        elevated ? "z-56" : "z-55"
      } ${center ? "md:justify-center" : ""}`}
    >
      <button
        type="button"
        aria-label="閉じる"
        className={`flex-1 ${center ? "md:absolute md:inset-0" : ""}`}
        onClick={onClose}
      />
      {/* 文字色は明示する。黒地の詳細表示から開いても白文字を継承しない。
          relative は center 時に絶対配置の背景ボタンへ潜り込まないため */}
      <div
        className={`relative flex max-h-[85vh] min-h-0 flex-col rounded-t-2xl pb-[env(safe-area-inset-bottom)] text-[var(--color-ink)] md:mx-auto md:w-full md:max-w-[var(--sheet-w)] md:rounded-2xl ${
          center ? "md:mb-0" : "md:mb-16"
        } ${tone === "bg" ? "bg-[var(--color-bg)]" : "bg-white"}`}
      >
        <header className="flex items-center justify-between border-b border-[var(--color-line)] p-4">
          <h2 className="text-sm font-semibold">{title}</h2>
          <div className="flex items-center gap-3">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="閉じる"
              className="-m-2 flex size-9 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
            >
              <X className="size-5 text-[var(--color-sub)]" aria-hidden />
            </button>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
